import React, { useState } from "react";
import { Button, Typography, Box } from "@mui/material";
/* eslint-disable no-restricted-imports -- Printed paper that mirrors InvoicePDF, not an app table; the package table's surface styling does not belong on it. */
import {
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  TableContainer,
} from "@mui/material";
/* eslint-enable no-restricted-imports */
import { Inline } from "components/layout/reusableLayouts";
import { formatCashFull, getCurrencySymbol } from "../../../utils/formatCash";
import { contactName } from "utils/fullName";
import { useTranslation } from "react-i18next";
import ViewOnlyAttachmentDrawer from "../contacts/ViewOnlyAttachmentDrawer";
import { InvoiceDiscountType } from "enums/InvoiceDiscountType";
import {
  FormikInvoice,
  PaymentTermsLabels,
} from "utils/invoices/invoiceFormat";
import CompanyAvatar from "components/common/CompanyAvatar";

interface InvoicePreviewProps {
  invoice: FormikInvoice;
  showShadow?: boolean;
  attachments?: any; // dont send in attachments except for on the externally visible pay invoice screen
}

const InvoicePreview: React.FC<InvoicePreviewProps> = ({
  invoice,
  showShadow = true,
  attachments,
}) => {
  const { t } = useTranslation();
  const [openAttachmentDrawer, setOpenAttachmentDrawer] = useState(false);

  const primaryRecipient = invoice?.primaryRecipient;
  // The unit beside a flat amount, which was the literal "$" while the figure
  // next to it already went through the invoice currency.
  const currencySymbol = getCurrencySymbol(invoice?.currency || "USD");

  const isNoRecipient =
    !primaryRecipient ||
    primaryRecipient === "null" ||
    (typeof primaryRecipient === "string" && primaryRecipient.trim() === "");

  const recipientName = isNoRecipient
    ? t("No Recipient Selected")
    : contactName(primaryRecipient);

  const recipientAddress = primaryRecipient?.address
    ? `${primaryRecipient.address.address1}, ${primaryRecipient.address.city}, ${primaryRecipient.address.state} ${primaryRecipient.address.zipcode}`
    : t("No Address");

  const recipientPhone = primaryRecipient?.phone || t("No Phone");
  const recipientEmail = primaryRecipient?.email || t("No Email");

  const companyLogo = invoice.companyDetails?.companyLogoUrl;
  const companyWebsite = invoice.companyDetails?.companyWebsite;

  // ANY CHANGES DONE TO THIS COMPONENT SHOULD BE REFLECTED IN THE InvoicePDF.tsx

  return (
    <Box
      // The paper is white in both schemes, so its ink has to be too. The
      // token record re-scopes to any subtree that names a scheme, and the
      // theme draws Typography in var(--color-text-primary), so this one
      // attribute carries every label, rule and caption below it. Without it
      // dark mode paints near-white text onto fixed-white paper.
      //
      // This supersedes the `color: var(--color-static-black)` main set here
      // for the same reason. That is an INHERITED colour, and the theme gives
      // every Typography its own `color: var(--color-text-primary)` rule,
      // which wins over inheritance. The document is almost entirely
      // Typography, so the borders went black and the words stayed invisible.
      data-theme="light"
      sx={{
        width: "210mm",
        minHeight: "297mm",
        padding: "10mm",
        marginInline: "auto",
        backgroundColor: "var(--color-bg-preview-light)",
        color: "var(--color-text-primary)",
        boxShadow: showShadow ? "var(--shadow-sm)" : "none",
        "@media print": {
          boxShadow: "none",
          marginInline: 0,
          width: "auto",
          minHeight: "32px",
          padding: "10mm",
        },
      }}
    >
      <Box>
        <Inline wrap={false} justify="between" align="start" gap="0">
          <Box>
            <Typography variant="h6">{invoice.invoiceName}</Typography>
            <Typography variant="body1">
              {invoice?.companyDetails?.companyName}
            </Typography>
            <Typography variant="body1">
              {invoice?.companyDetails?.companyPhone}
            </Typography>
            <Typography variant="body1">
              {invoice?.companyDetails?.companyWebsite}
            </Typography>
            <Typography variant="body1">
              {invoice?.companyDetails?.companyEmail}
            </Typography>
          </Box>
          <Box>
            {companyLogo ? (
              <img
                src={companyLogo}
                alt="Company Logo"
                style={{ maxHeight: "70px", maxWidth: "150px" }}
              />
            ) : (
              <CompanyAvatar
                name={invoice?.companyDetails?.companyName || ""}
                website={companyWebsite}
                size="xl"
              />
            )}
          </Box>
        </Inline>
        <Inline wrap={false} justify="between" align="start" gap="0">
          <Box sx={{ width: "50%", paddingRight: "var(--space-4)" }}>
            <Inline wrap={false} justify="between" gap="0">
              <Typography variant="body1">{t("Invoice Number")}:</Typography>
              <Typography variant="body1">{invoice.invoiceNumber}</Typography>
            </Inline>
            {invoice.purchaseOrderId && (
              <Inline wrap={false} justify="between" gap="0">
                <Typography variant="body1">
                  {t("Purchase Order Number")}:
                </Typography>
                <Typography variant="body1">
                  {invoice.purchaseOrderId}
                </Typography>
              </Inline>
            )}
            <Inline wrap={false} justify="between" gap="0">
              <Typography variant="body1">{t("Invoice Date")}:</Typography>
              <Typography variant="body1">
                {new Date(invoice.invoiceDate).toDateString()}
              </Typography>
            </Inline>
            <Inline wrap={false} justify="between" gap="0">
              <Typography variant="body1">{t("Due Date")}:</Typography>
              {invoice.dueDate ? (
                <Typography variant="body1">
                  {new Date(invoice.dueDate).toDateString()}
                </Typography>
              ) : (
                <Typography variant="body1">
                  {PaymentTermsLabels[invoice.paymentTerms]}
                </Typography>
              )}
            </Inline>
          </Box>

          <Box sx={{ width: "50%", paddingLeft: "var(--space-4)" }}>
            <Inline wrap={false} justify="between" gap="0">
              <Typography variant="body1">{t("Customer")}:</Typography>
              <Typography
                variant="body1"
                sx={{
                  display: "flex",
                  flexDirection: "column",
                  textAlign: "right",
                  marginLeft: "auto",
                }}
              >
                {recipientName}
              </Typography>
            </Inline>

            <Inline wrap={false} justify="between" gap="0">
              <Typography variant="body1">{t("Address")}:</Typography>
              <Typography
                variant="body1"
                sx={{
                  display: "flex",
                  flexDirection: "column",
                  textAlign: "right",
                  marginLeft: "auto",
                }}
              >
                {recipientAddress}
              </Typography>
            </Inline>
            <Inline wrap={false} justify="between" gap="0">
              <Typography variant="body1">{t("Phone")}:</Typography>
              <Typography
                variant="body1"
                sx={{
                  display: "flex",
                  textAlign: "right",
                  marginLeft: "auto",
                }}
              >
                {recipientPhone}
              </Typography>
            </Inline>
            <Inline wrap={false} justify="between" gap="0">
              <Typography variant="body1">{t("Email")}:</Typography>
              <Typography
                variant="body1"
                sx={{
                  display: "flex",
                  textAlign: "right",
                  marginLeft: "auto",
                }}
              >
                {recipientEmail}
              </Typography>
            </Inline>
          </Box>
        </Inline>
      </Box>

      <TableContainer sx={{ marginTop: "var(--space-6)" }}>
        <Table>
          <TableHead>
            <TableRow>
              {/* Product and Description split what the fixed columns leave, as in the PDF. */}
              <TableCell sx={{ width: "30%", padding: "var(--space-3)" }}>
                <Typography variant="subtitle2">
                  {t("Product or Service")}
                </Typography>
              </TableCell>
              <TableCell sx={{ padding: "var(--space-3)" }}>
                <Typography variant="subtitle2">{t("Description")}</Typography>
              </TableCell>
              <TableCell
                align="center"
                sx={{ width: "40px", padding: "var(--space-3)" }}
              >
                <Typography variant="subtitle2">{t("Qty")}</Typography>
              </TableCell>
              <TableCell
                align="right"
                sx={{ width: "120px", padding: "var(--space-3)" }}
              >
                <Typography variant="subtitle2">{t("Unit Price")}</Typography>
              </TableCell>
              <TableCell
                align="right"
                sx={{ width: "120px", padding: "var(--space-3)" }}
              >
                <Typography variant="subtitle2">{t("Total")}</Typography>
              </TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {invoice.details.map((detail, index) => (
              <TableRow key={index}>
                <TableCell sx={{ padding: "var(--space-3)" }}>
                  <Typography
                    variant="body2"
                    sx={{ whiteSpace: "normal", overflowWrap: "break-word" }}
                  >
                    {detail.productOrService}
                  </Typography>
                </TableCell>
                <TableCell sx={{ padding: "var(--space-3)" }}>
                  <Typography
                    variant="body2"
                    sx={{ whiteSpace: "normal", overflowWrap: "break-word" }}
                  >
                    {detail.description}
                  </Typography>
                </TableCell>
                <TableCell align="center" sx={{ padding: "var(--space-3)" }}>
                  <Typography variant="body2">{detail.quantity}</Typography>
                </TableCell>
                <TableCell align="right" sx={{ padding: "var(--space-3)" }}>
                  <Typography variant="body2">
                    {formatCashFull(detail.amount, invoice.currency)}
                  </Typography>
                </TableCell>
                <TableCell align="right" sx={{ padding: "var(--space-3)" }}>
                  <Typography variant="body2">
                    {formatCashFull(detail.total, invoice.currency)}
                  </Typography>
                </TableCell>
              </TableRow>
            ))}

            <TableRow
              sx={{ borderTop: "2px solid var(--color-border-strong)" }}
            >
              <TableCell
                sx={{ border: "none", padding: "var(--space-3)" }}
                colSpan={3}
              />
              <TableCell sx={{ padding: "var(--space-3)" }}>
                {t("Subtotal")}
              </TableCell>
              <TableCell align="right" sx={{ padding: "var(--space-3)" }}>
                {formatCashFull(invoice.subtotal, invoice.currency)}
              </TableCell>
            </TableRow>

            {(invoice.discountAmount! > 0 || invoice.discountAmount! < 0) && (
              <TableRow>
                <TableCell
                  sx={{ border: "none", padding: "var(--space-3)" }}
                  colSpan={3}
                />
                <TableCell sx={{ padding: "var(--space-3)" }}>
                  {t("Discount")}{" "}
                  {invoice.discountType === InvoiceDiscountType.PERCENTAGE
                    ? `${invoice.discount}%`
                    : currencySymbol}
                </TableCell>
                <TableCell sx={{ padding: "var(--space-3)" }} align="right">
                  {formatCashFull(invoice.discountAmount, invoice.currency)}
                </TableCell>
              </TableRow>
            )}

            {(invoice.taxAmount! > 0 || invoice.taxAmount! < 0) && (
              <TableRow>
                <TableCell
                  sx={{ border: "none", padding: "var(--space-3)" }}
                  colSpan={3}
                />
                <TableCell sx={{ padding: "var(--space-3)" }}>
                  {t("Tax")}{" "}
                  {invoice.taxType === InvoiceDiscountType.PERCENTAGE
                    ? `${invoice.tax}%`
                    : currencySymbol}
                </TableCell>
                <TableCell sx={{ padding: "var(--space-3)" }} align="right">
                  {formatCashFull(invoice.taxAmount, invoice.currency)}
                </TableCell>
              </TableRow>
            )}

            {(invoice.serviceFeeAmount! > 0 ||
              invoice.serviceFeeAmount! < 0) && (
              <TableRow>
                <TableCell
                  sx={{ border: "none", padding: "var(--space-3)" }}
                  colSpan={3}
                />
                <TableCell sx={{ padding: "var(--space-3)" }}>
                  {t("Service Fee")}{" "}
                  {invoice.serviceFeeType === InvoiceDiscountType.PERCENTAGE
                    ? `${invoice.serviceFee}%`
                    : currencySymbol}
                </TableCell>
                <TableCell sx={{ padding: "var(--space-3)" }} align="right">
                  {formatCashFull(invoice.serviceFeeAmount, invoice.currency)}
                </TableCell>
              </TableRow>
            )}

            <TableRow>
              <TableCell
                sx={{ border: "none", padding: "var(--space-3)" }}
                colSpan={3}
              />
              <TableCell sx={{ padding: "var(--space-3)" }}>
                <Typography variant="subtitle2">{t("Total")}</Typography>
              </TableCell>
              <TableCell sx={{ padding: "var(--space-3)" }} align="right">
                <Typography variant="subtitle2">
                  {formatCashFull(invoice.total, invoice.currency)}
                </Typography>
              </TableCell>
            </TableRow>
            <TableRow>
              <TableCell
                sx={{ border: "none", padding: "var(--space-3)" }}
                colSpan={3}
              />

              {attachments && attachments.length > 0 && (
                <TableCell
                  sx={{
                    border: "none",
                    padding: "var(--space-3)",
                    textAlign: "center",
                    "@media print": { display: "none" },
                  }}
                  colSpan={2}
                >
                  <Inline
                    wrap={false}
                    justify="center"
                    gap="0"
                    sx={{ width: "100%" }}
                  >
                    <Button
                      variant="primary-outlined"
                      size="large"
                      onClick={() => setOpenAttachmentDrawer(true)}
                    >
                      {t("View Attachments")}
                    </Button>
                  </Inline>
                </TableCell>
              )}
            </TableRow>
          </TableBody>
        </Table>
      </TableContainer>

      {invoice.note && (
        <Box sx={{ width: "100%" }}>
          <Typography variant="body1">{t("Notes")}: </Typography>
          <Typography variant="body1" sx={{ whiteSpace: "pre-wrap" }}>
            {invoice.note}
          </Typography>
        </Box>
      )}

      {/* this attachment drawer will only be viewable on the paying an invoice 
      screen a customer will come to to pay an invoice */}

      {/* Stays mounted, so it slides out: it fetches nothing until a file is printed or downloaded. */}
      {invoice.id && (
        <ViewOnlyAttachmentDrawer
          openAttachmentDrawer={openAttachmentDrawer}
          handleCloseAttachmentDrawer={() => setOpenAttachmentDrawer(false)}
          drawerTitle={t("Attachments")}
          localAssets={attachments}
        />
      )}
    </Box>
  );
};

export default InvoicePreview;
